import { Checkbox } from "@dropbox/dig-components/controls";
import { Menu } from "@dropbox/dig-components/menu";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { Truncate } from "@dropbox/dig-components/truncate";
import { UIIcon } from "@dropbox/dig-icons";
import { SearchLine } from "@dropbox/dig-icons/assets";
import { FavIcon } from "@mirage/link-list/Favicon/Favicon";
import { ListItemSize } from "@mirage/link-list/types";
import type { TabInfo } from "@mirage/service-extension-tab-ingestion/service/types";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import { faviconSrcForSrcUrl } from "@mirage/shared/util/favicon";
import { getTimeAgoString } from "@mirage/shared/util/time";
import { urlToDomainName } from "@mirage/shared/util/urlRulesUtil";
import i18n from "@mirage/translations";
import { memo, useCallback, useMemo, useState } from "react";
import styles from "./BrowserTabsPickerMenu.module.css";

interface BrowserTabsPickerMenuItemsProps {
  windowTabInfos: Map<number, TabInfo[]>;
  sortedTabInfos: TabInfo[];
  selectedTabIDs: Set<number>;
  setSelectedTabIDs: (tabIDs: Set<number>) => void;
}
export const BrowserTabsPickerMenuItems = memo(
  ({
    windowTabInfos,
    sortedTabInfos,
    selectedTabIDs,
    setSelectedTabIDs,
  }: BrowserTabsPickerMenuItemsProps) => {
    const handleToggleTabID = useCallback(
      (tabID: number) => {
        const newSelectedTabIDs = new Set(selectedTabIDs);
        if (newSelectedTabIDs.has(tabID)) {
          newSelectedTabIDs.delete(tabID);
        } else {
          newSelectedTabIDs.add(tabID);
        }
        setSelectedTabIDs(newSelectedTabIDs);
      },
      [selectedTabIDs, setSelectedTabIDs],
    );

    if (sortedTabInfos.length === 0) {
      return null;
    }
    return (
      <Menu.Segment
        withLabel={i18n.t("context_picker_browser_tabs_recent_tabs_label")}
      >
        {sortedTabInfos.slice(0, 5).map((tabInfo) => {
          const isSelected = selectedTabIDs.has(tabInfo.id);
          return (
            <BrowserTabsPickerMenuItem
              key={tabInfo.id}
              tabInfo={tabInfo}
              isSelected={isSelected}
              onToggle={handleToggleTabID}
            />
          );
        })}
        <BrowserTabsAllTabsSubmenu
          windowTabInfos={windowTabInfos}
          selectedTabIDs={selectedTabIDs}
          onToggleTabID={handleToggleTabID}
        />
      </Menu.Segment>
    );
  },
);
BrowserTabsPickerMenuItems.displayName = "BrowserTabsPickerMenuItems";

interface BrowserTabsPickerMenuItemProps {
  tabInfo: TabInfo;
  isSelected: boolean;
  onToggle: (tabID: number) => void;
}
export const BrowserTabsPickerMenuItem = memo(
  ({ tabInfo, isSelected, onToggle }: BrowserTabsPickerMenuItemProps) => {
    const isDarkMode = useTheme();
    return (
      <Menu.SelectItem
        key={tabInfo.id}
        className={styles.BrowserTabsPickerMenuItemRow}
        role="menuitemcheckbox"
        aria-checked={isSelected}
        withLeftAccessory={
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              onToggle(tabInfo.id);
              e.stopPropagation();
            }}
            tabIndex={-1}
          />
        }
        withRightAccessory={
          <FavIcon
            src={faviconSrcForSrcUrl(tabInfo.url || "", 32, isDarkMode)}
            size={ListItemSize.Small}
          />
        }
        withSubtitle={
          <div className={styles.BrowserTabsPickerMenuItemSubtitle}>
            <div className={styles.BrowserTabsPickerMenuItemSubtitleDomain}>
              {urlToDomainName(tabInfo.url || "")}
            </div>
            <div className={styles.BrowserTabsPickerMenuItemSubtitleDivider}>
              ·
            </div>
            <div className={styles.BrowserTabsPickerMenuItemSubtitleTime}>
              {getTimeAgoString(tabInfo.lastAccessed, {
                abbreviateTime: true,
                abbreviateDate: true,
              })}
            </div>
          </div>
        }
        onClick={(e) => {
          onToggle(tabInfo.id);
          e.stopPropagation();
        }}
      >
        <Truncate lines={1}>{tabInfo.title}</Truncate>
      </Menu.SelectItem>
    );
  },
);
BrowserTabsPickerMenuItem.displayName = "BrowserTabsPickerMenuItem";

interface BrowserTabsAllTabsSubmenuProps {
  windowTabInfos: Map<number, TabInfo[]>;
  selectedTabIDs: Set<number>;
  onToggleTabID: (tabID: number) => void;
}
export const BrowserTabsAllTabsSubmenu = memo(
  ({
    windowTabInfos,
    selectedTabIDs,
    onToggleTabID,
  }: BrowserTabsAllTabsSubmenuProps) => {
    const [searchText, setSearchText] = useState("");
    const filteredTabInfos = useMemo(() => {
      const filteredEntries: Array<[number, TabInfo[]]> = Array.from(
        windowTabInfos.entries(),
      ).map(([windowID, tabInfos]) => {
        const filteredTabInfos = tabInfos.filter((tabInfo) =>
          isTabInfoMatchingSearchText(tabInfo, searchText),
        );
        return [windowID, filteredTabInfos];
      });
      return filteredEntries.filter(
        ([_windowID, tabInfos]) => tabInfos.length > 0,
      );
    }, [windowTabInfos, searchText]);

    return (
      <Menu.Submenu
        contentClassName={styles.BrowserTabsPickerMenuSubmenuContent}
        withTriggerContent={i18n.t(
          "context_picker_browser_tabs_all_tabs_label",
        )}
        closeOnSelection={false}
      >
        <div className={styles.BrowserTabsPickerMenuSubmenuContentInner}>
          <div className={styles.BrowserTabsPickerMenuSubmenuContentScrollable}>
            {filteredTabInfos.map(([windowID, tabInfos], i) => (
              <Menu.Segment
                key={windowID}
                withLabel={i18n.t("context_picker_browser_tabs_window_label", {
                  index: i + 1,
                })}
              >
                {tabInfos.map((tabInfo) => {
                  const isSelected = selectedTabIDs.has(tabInfo.id);
                  return (
                    <BrowserTabsPickerMenuItem
                      key={tabInfo.id}
                      tabInfo={tabInfo}
                      isSelected={isSelected}
                      onToggle={onToggleTabID}
                    />
                  );
                })}
              </Menu.Segment>
            ))}
          </div>
          <Menu.Segment
            className={styles.BrowserTabsPickerMenuSubmenuSearchSegment}
          >
            <Menu.Row
              className={styles.BrowserTabsPickerMenuSubmenuSearch}
              withLeftAccessory={<UIIcon src={SearchLine} />}
            >
              <TextInput
                placeholder={i18n.t(
                  "context_picker_browser_tabs_search_placeholder",
                )}
                size="standard"
                isTransparent
                value={searchText}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearchText(e.target.value);
                }}
                onKeyDown={(e) => {
                  // prevent menu standard typeahead behavior from triggering
                  e.stopPropagation();
                }}
              />
            </Menu.Row>
          </Menu.Segment>
        </div>
      </Menu.Submenu>
    );
  },
);
BrowserTabsAllTabsSubmenu.displayName = "BrowserTabsAllTabsSubmenu";

function isTabInfoMatchingSearchText(tabInfo: TabInfo, searchText: string) {
  const searchTextLower = searchText.toLowerCase();
  if (tabInfo.title?.toLowerCase().includes(searchTextLower)) {
    return true;
  }
  if (tabInfo.url?.toLowerCase().includes(searchTextLower)) {
    return true;
  }
  return false;
}
